import { Box, Stack, Typography, Switch, MenuItem, TextField, Button } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { SearchNormal1 } from 'iconsax-react';
import MainCard from 'components/MainCard';
import Loader2 from 'components/Loader2';
import CustomTable from 'components/@extended/CustomTable';
import IconButton from 'components/@extended/IconButton';
import useAuth from 'hooks/useAuth';
import { useEffect, useMemo, useState, useCallback } from 'react';
import Snackbar from 'utils/Snackbar';
import { getAllClassRoom } from 'services/classRoom.service';
import { getAllSubjects } from 'services/subject.service';
import { getCustomUser } from 'services/user.service';
import { saveAttendance, updateAttendance, getAttendanceReport } from 'services/attendance.service';
import { getAllBatch, BatchwiseCourse } from 'services/batch.service';
import { LocalizationProvider, MobileDatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLocation } from 'react-router';
import { format, isValid } from 'date-fns';

/* ---------- Types ---------- */

interface AttendanceRecord {
  _id: string;
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  rollNumber?: string;
  present: boolean;
  originalPresent?: boolean;
  isSaved?: boolean;
}

/* ---------- Helpers ---------- */

const safeDate = (value?: string) => {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? d : null;
};

const safeTime = (value?: string) => {
  if (!value) return null;
  const d = new Date(`1970-01-01T${value}`);
  return isValid(d) ? d : null;
};

const formatDateOnly = (date: Date | null) => (date && isValid(date) ? format(date, 'yyyy-MM-dd') : '');

const formatTimeOnly = (date: Date | null) => (date && isValid(date) ? format(date, 'HH:mm') : '');

/* ---------- Switch Cell ---------- */

const AttendanceCell = ({ row, onToggle }: any) => {
  const data = row.row.original;
  return <Switch checked={data.present} onChange={() => onToggle(data.studentId)} color="success" />;
};

/* ---------- Main Component ---------- */

const AttendanceCheck = () => {
  const { user, organization } = useAuth();
  const orgId = user?.activeOrganizationId || user?.organizationId?._id;
  const ncvetCompliance = organization?.ncvetCompliance === true;

  const location = useLocation();
  const state = location.state as any;

  const [classRooms, setClassRooms] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [batchList, setBatchList] = useState<any[]>([]);

  const [selectedClass, setSelectedClass] = useState(state?.classRoomId || '');
  const [selectedSubject, setSelectedSubject] = useState(state?.subjectId || '');
  const [selectedBatchId, setSelectedBatchId] = useState(state?.batchId || '');

  const [selectedDate, setSelectedDate] = useState<Date | null>(safeDate(state?.date));
  const [startTime, setStartTime] = useState<Date | null>(safeTime(state?.startTime));
  const [endTime, setEndTime] = useState<Date | null>(safeTime(state?.endTime));

  const [students, setStudents] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [size, setSize] = useState(10);

  const isEditMode = ncvetCompliance
    ? !!selectedBatchId && !!selectedDate && !!startTime && !!endTime
    : !!selectedClass && !!selectedSubject && !!selectedDate && !!startTime && !!endTime;

  /* ---------- Load Class, Subjects & Batches ---------- */

  useEffect(() => {
    if (!orgId) return;

    if (ncvetCompliance) {
      getAllBatch({ orgId, limit: 500, skip: 1, deleted: false })
        .then((res: any) => {
          const list = res?.data?.data?.batchData || [];
          setBatchList(Array.isArray(list) ? list : []);
        })
        .catch(() => Snackbar('Failed to load batches', 'error'));
    } else {
      Promise.all([getAllClassRoom({ orgId }), getAllSubjects({ orgId })])
        .then(([clsRes, subRes]: any) => {
          setClassRooms(clsRes?.data?.data?.classRoomData || []);
          setSubjects(subRes?.data?.data?.data || []);
        })
        .catch(() => Snackbar('Failed to load class or subjects', 'error'));
    }
  }, [orgId, ncvetCompliance]);

  /* ---------- Load Students (by class or by batch) ---------- */

  useEffect(() => {
    if (ncvetCompliance) {
      if (!selectedBatchId) {
        setStudents([]);
        return;
      }
      setLoading(true);
      BatchwiseCourse({ batchId: selectedBatchId })
        .then((res: any) => {
          const details = res?.data?.data;
          const rawStudents = Array.isArray(details?.students) ? details.students : [];
          const approved = rawStudents.filter((s: any) => s.status === 'approve');
          const mapped: AttendanceRecord[] = approved.map((s: any) => ({
            _id: s.studentId || s._id,
            studentId: s.studentId || s._id,
            firstName: s.firstName ?? '',
            lastName: s.lastName ?? '',
            email: s.email ?? '',
            rollNumber: s.studentNumber ?? s.rollNumber,
            present: true,
            isSaved: false
          }));
          setStudents(mapped);
        })
        .catch(() => Snackbar('Failed to load batch students', 'error'))
        .finally(() => setLoading(false));
      return;
    }

    if (!selectedClass) {
      setStudents([]);
      return;
    }

    setLoading(true);

    (async () => {
      try {
        const stuRes: any = await getCustomUser({
          classRoomId: selectedClass,
          orgId,
          role: 'student'
        });

        const usersArray = Array.isArray(stuRes?.data?.data?.totalData) ? stuRes.data.data.totalData : [];

        const mapped = usersArray.map((s: any) => ({
          _id: s._id,
          studentId: s._id,
          firstName: s.firstName,
          lastName: s.lastName,
          email: s.email,
          rollNumber: s.rollNumber,
          present: true,
          isSaved: false
        }));

        setStudents(mapped);
      } catch {
        Snackbar('Failed to load students', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedClass, selectedBatchId, orgId, ncvetCompliance]);

  /* ---------- Load Existing Attendance ---------- */

  useEffect(() => {
    const loadExisting = async () => {
      if (!isEditMode || !selectedDate || !startTime || !endTime || students.length === 0) return;
      if (ncvetCompliance ? !selectedBatchId : !selectedClass || !selectedSubject) return;

      setLoading(true);
      try {
        const params: any = {
          date: formatDateOnly(selectedDate),
          startTime: formatTimeOnly(startTime),
          endTime: formatTimeOnly(endTime)
        };
        if (ncvetCompliance) {
          params.batchId = selectedBatchId;
        } else {
          params.classRoomId = selectedClass;
          params.subjectId = selectedSubject;
        }
        const res = await getAttendanceReport(params);

        const existing = Array.isArray(res.data?.data) ? res.data.data : [];

        /* ---- If no attendance exists for filters ---- */
        if (existing.length === 0) {
          setStudents((prev) =>
            prev.map((s) => ({
              ...s,
              present: true,
              originalPresent: undefined,
              isSaved: false
            }))
          );
          return;
        }

        /* ---- If attendance exists ---- */
        setStudents((prev) =>
          prev.map((s) => {
            const sid = typeof s.studentId === 'object' ? (s.studentId as any)?._id : s.studentId;
            const found = existing.find((e: any) => (e.studentId?._id ?? e.studentId) === sid);
            return found
              ? {
                  ...s,
                  _id: found._id,
                  present: found.present,
                  originalPresent: found.present,
                  isSaved: true
                }
              : {
                  ...s,
                  present: true,
                  originalPresent: undefined,
                  isSaved: false
                };
          })
        );
      } catch {
        Snackbar('Failed to load attendance', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadExisting();
  }, [isEditMode, selectedClass, selectedSubject, selectedBatchId, selectedDate, startTime, endTime, students.length, ncvetCompliance]);

  /* ---------- Toggle ---------- */

  const handleToggle = useCallback((id: string) => {
    setStudents((prev) => prev.map((s) => (s.studentId === id ? { ...s, present: !s.present } : s)));
  }, []);

  /* ---------- Save ---------- */

  const handleSubmit = async () => {
    const userId = user?.userTypeId?._id || user?._id;
    if (ncvetCompliance) {
      if (!selectedBatchId || !selectedDate || !startTime || !endTime) {
        Snackbar('Please select batch, date and time', 'warning');
        return;
      }
    } else {
      if (!selectedClass || !selectedSubject || !selectedDate || !startTime || !endTime) {
        Snackbar('Please select all fields', 'warning');
        return;
      }
    }

    try {
      setLoading(true);

      if (ncvetCompliance) {
        /* ---- NCVET: batch-wise save ---- */
        if (isEditMode) {
          const changedRows = students.filter((s) => s.isSaved && s.present !== s.originalPresent);
          const newRows = students.filter((s) => !s.isSaved);
          const updateCalls = changedRows.map((s) =>
            updateAttendance(s._id, { present: s.present, updatedBy: userId })
          );
          const saveCalls =
            newRows.length > 0
              ? [
                  saveAttendance({
                    batchId: selectedBatchId,
                    date: formatDateOnly(selectedDate),
                    startTime: formatTimeOnly(startTime),
                    endTime: formatTimeOnly(endTime),
                    students: newRows.map((s) => ({ studentId: s.studentId, present: s.present })),
                    updatedBy: userId
                  })
                ]
              : [];
          await Promise.all([...updateCalls, ...saveCalls]);
          Snackbar('Attendance updated successfully', 'success');
        } else {
          await saveAttendance({
            batchId: selectedBatchId,
            date: formatDateOnly(selectedDate),
            startTime: formatTimeOnly(startTime),
            endTime: formatTimeOnly(endTime),
            students: students.map((s) => ({ studentId: s.studentId, present: s.present })),
            updatedBy: userId
          });
          Snackbar('Attendance saved successfully', 'success');
        }
      } else {
        /* ---- Non-NCVET: class + subject ---- */
        if (isEditMode) {
          const changedRows = students.filter((s) => s.isSaved && s.present !== s.originalPresent);
          const newRows = students.filter((s) => !s.isSaved);
          const updateCalls = changedRows.map((s) =>
            updateAttendance(s._id, { present: s.present, updatedBy: userId })
          );
          const saveCalls =
            newRows.length > 0
              ? [
                  saveAttendance({
                    classRoomId: selectedClass,
                    subjectId: selectedSubject,
                    date: formatDateOnly(selectedDate),
                    startTime: formatTimeOnly(startTime),
                    endTime: formatTimeOnly(endTime),
                    students: newRows.map((s) => ({ studentId: s.studentId, present: s.present })),
                    updatedBy: userId
                  })
                ]
              : [];
          await Promise.all([...updateCalls, ...saveCalls]);
          Snackbar('Attendance updated successfully', 'success');
        } else {
          await saveAttendance({
            classRoomId: selectedClass,
            subjectId: selectedSubject,
            date: formatDateOnly(selectedDate),
            startTime: formatTimeOnly(startTime),
            endTime: formatTimeOnly(endTime),
            students: students.map((s) => ({ studentId: s.studentId, present: s.present })),
            updatedBy: userId
          });
          Snackbar('Attendance saved successfully', 'success');
        }
      }

      setStudents((prev) =>
        prev.map((s) => ({ ...s, isSaved: true, originalPresent: s.present }))
      );
    } catch {
      Snackbar('Failed to save attendance', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setStudents((prev) =>
      prev.map((s) => ({
        ...s,
        present: true,
        originalPresent: undefined,
        isSaved: false
      }))
    );
  }, [selectedSubject, selectedDate, startTime, endTime]);

  /* ---------- Search & Pagination (client-side) ---------- */

  const filteredStudents = useMemo(() => {
    if (!search.trim()) return students;
    const q = search.trim().toLowerCase();
    return students.filter((s) => {
      const first = (s.firstName ?? '').trim();
      const last = (s.lastName ?? '').trim();
      const fullName = `${first} ${last}`.trim().toLowerCase();
      const fullNameReversed = `${last} ${first}`.trim().toLowerCase();
      return (
        first.toLowerCase().includes(q) ||
        last.toLowerCase().includes(q) ||
        fullName.includes(q) ||
        fullNameReversed.includes(q) ||
        (s.email ?? '').toLowerCase().includes(q) ||
        (String(s.rollNumber ?? '')).toLowerCase().includes(q)
      );
    });
  }, [students, search]);

  const paginatedStudents = useMemo(() => {
    const start = (pageIndex - 1) * size;
    return filteredStudents.slice(start, start + size);
  }, [filteredStudents, pageIndex, size]);

  useEffect(() => {
    setPageIndex(1);
  }, [search, students.length]);

  /* ---------- Table Columns ---------- */

  const columns: any = useMemo(
    () => [
      { Header: 'Roll No', accessor: 'rollNumber' },
      {
        Header: 'Student Name',
        Cell: ({ row }: any) => (
          <Typography>
            {row.original.firstName} {row.original.lastName}
          </Typography>
        )
      },
      { Header: 'Email', accessor: 'email' },
      {
        Header: 'Attendance',
        Cell: (row: any) => <AttendanceCell row={row} onToggle={handleToggle} />
      }
    ],
    [handleToggle]
  );

  /* ---------- UI ---------- */

  return (
    <MainCard
      title="Student Attendance"
      secondary={
        students.length > 0 ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search"
              size="small"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPageIndex(1);
                  setSearch(searchInput);
                }
              }}
            />
            <IconButton
              variant="outlined"
              onClick={() => {
                setPageIndex(1);
                setSearch(searchInput);
              }}
            >
              <SearchNormal1 />
            </IconButton>
          </Stack>
        ) : null
      }
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3} flexWrap="wrap">
        {ncvetCompliance ? (
          <TextField
            select
            label="Select Batch"
            value={selectedBatchId}
            onChange={(e) => setSelectedBatchId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Select</MenuItem>
            {batchList.map((b) => {
              const label = [b.name].filter(Boolean).join(' ') || b._id;
              return (
                <MenuItem key={b._id} value={b._id}>
                  {label}
                </MenuItem>
              );
            })}
          </TextField>
        ) : (
          <>
            <TextField
              select
              label="Select Class"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Select</MenuItem>
              {classRooms.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.className} ({c.divisionName})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Select Subject"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              sx={{ minWidth: 200 }}
              disabled={!selectedClass}
            >
              <MenuItem value="">Select</MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {s.subjectName}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MobileDatePicker
            label="Select Date"
            value={selectedDate}
            onChange={(v) => setSelectedDate(v)}
            maxDate={new Date()}
            disabled={ncvetCompliance ? !selectedBatchId : !selectedClass}
          />
          <TimePicker
            label="Start Time"
            value={startTime}
            onChange={(v) => setStartTime(v)}
            disabled={ncvetCompliance ? !selectedBatchId : !selectedClass}
          />
          <TimePicker
            label="End Time"
            value={endTime}
            onChange={(v) => setEndTime(v)}
            disabled={ncvetCompliance ? !selectedBatchId : !selectedClass}
          />
        </LocalizationProvider>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" height="40vh">
          <Loader2 />
        </Box>
      ) : students.length === 0 ? (
        <Box display="flex" justifyContent="center" height="40vh" alignItems="center">
          <Typography>
            {ncvetCompliance
              ? selectedBatchId
                ? 'No approved students in this batch'
                : 'Please select batch, date and time'
              : selectedClass
                ? 'No students found'
                : 'Please select class & date/time'}
          </Typography>
        </Box>
      ) : (
        <>
          <CustomTable
            columns={columns}
            data={paginatedStudents}
            sortArray={[]}
            sort={[]}
            setSortingData={() => {}}
            search={search}
            notFound={false}
            count={filteredStudents.length}
            size={size}
            pageIndex={pageIndex}
            handlePageChange={(_e: any, page: number) => setPageIndex(page)}
            handleSizeChange={(e: SelectChangeEvent<number>) => {
              setSize(Number(e.target.value));
              setPageIndex(1);
            }}
          />

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="contained" color="success" onClick={handleSubmit}>
              Save Attendance
            </Button>
          </Box>
        </>
      )}
    </MainCard>
  );
};

export default AttendanceCheck;
